import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRightIcon, HomeIcon, MapPinIcon, EuroIcon, PhoneIcon, UsersIcon, EyeIcon, PlusCircleIcon, CalendarIcon, CheckCircleIcon, ExternalLinkIcon, EditIcon, ShareIcon, LinkIcon, ImageIcon, UserIcon, XIcon, InfoIcon } from 'lucide-react';
import { useRental } from '../../contexts/RentalContext';
export function ListingDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    getListingById,
    tenantLeads,
    viewings,
    prospectingLeads
  } = useRental();
  const [showShareModal, setShowShareModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const listing = getListingById(id || '');
  const listingLeads = tenantLeads.filter(lead => lead.associatedListings.includes(id || ''));
  const listingViewings = viewings.filter(v => v.listingId === id);
  const sourceProspectingLead = prospectingLeads.find(l => l.listingId === id);
  if (!listing) {
    return <div className="w-full min-h-screen bg-[#F5F7FB] flex items-center justify-center" data-id="element-10049">
        <div className="text-center" data-id="element-10050">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-10051">
            Anunț negăsit
          </h2>
          <Link to="/inchiriere/anunturi" className="text-[#357BEB] hover:underline" data-id="element-10052">
            Înapoi la anunțuri
          </Link>
        </div>
      </div>;
  }
  const publicPreviewUrl = `${window.location.origin}/preview/${listing.id}`;
  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicPreviewUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-[#21A366] text-white';
      case 'draft':
        return 'bg-gray-400 text-white';
      case 'rented':
        return 'bg-[#357BEB] text-white';
      default:
        return 'bg-gray-200 text-gray-700';
    }
  };
  const getViewingStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-[#357BEB] text-white';
      case 'confirmed':
        return 'bg-[#21A366] text-white';
      case 'completed':
        return 'bg-gray-600 text-white';
      case 'cancelled':
        return 'bg-gray-400 text-white';
      case 'no_show':
        return 'bg-red-500 text-white';
      default:
        return 'bg-gray-200 text-gray-700';
    }
  };
  return <div className="w-full min-h-screen bg-[#F5F7FB]" data-id="element-10053">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200" data-id="element-10054">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-10055">
          <div className="flex items-center space-x-2 text-sm" data-id="element-10056">
            <Link to="/inchiriere" className="text-[#357BEB] hover:underline font-medium" data-id="element-10057">
              ÎNCHIRIERE
            </Link>
            <ChevronRightIcon className="w-4 h-4 text-gray-400" data-id="element-10058" />
            <Link to="/inchiriere/anunturi" className="text-[#357BEB] hover:underline font-medium" data-id="element-10059">
              Anunțuri
            </Link>
            <ChevronRightIcon className="w-4 h-4 text-gray-400" data-id="element-10060" />
            <span className="text-gray-600" data-id="element-10061">{listing.id}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-10062">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-10063">
          {/* Left Column - Property Details */}
          <div className="lg:col-span-2 space-y-6" data-id="element-10064">
            {/* Image Gallery */}
            {listing.images && listing.images.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-white rounded-xl shadow-md overflow-hidden" data-id="element-10065">
                <div className="aspect-[16/9] bg-gray-200 relative" data-id="element-10066">
                  <img src={listing.images[0]} alt="Imagine principală" className="w-full h-full object-cover" data-id="element-10067" />
                  {listing.images.length > 1 && <div className="absolute bottom-4 right-4 bg-black bg-opacity-70 text-white px-3 py-2 rounded-lg text-sm font-semibold flex items-center space-x-2" data-id="element-10068">
                      <ImageIcon className="w-4 h-4" data-id="element-10069" />
                      <span data-id="element-10070">{listing.images.length} fotografii</span>
                    </div>}
                </div>
                {listing.images.length > 1 && <div className="p-4 grid grid-cols-4 gap-2" data-id="element-10071">
                    {listing.images.slice(1, 5).map((img, idx) => <div key={idx} className="aspect-square bg-gray-200 rounded-lg overflow-hidden" data-id="element-10072">
                        <img src={img} alt={`Imagine ${idx + 2}`} className="w-full h-full object-cover" data-id="element-10073" />
                      </div>)}
                  </div>}
              </motion.div>}

            {/* Header Card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-white rounded-xl shadow-md p-6" data-id="element-10074">
              <div className="flex items-start justify-between mb-4" data-id="element-10075">
                <div data-id="element-10076">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-10077">
                    {listing.propertyType.charAt(0).toUpperCase() + listing.propertyType.slice(1)}{' '}
                    {listing.rooms} camere
                  </h1>
                  <div className="flex items-center text-gray-600 mb-3" data-id="element-10078">
                    <MapPinIcon className="w-5 h-5 mr-2" data-id="element-10079" />
                    <span data-id="element-10080">{listing.address || listing.zone}</span>
                  </div>
                </div>
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(listing.status)}`} data-id="element-10081">
                  {listing.status === 'active' ? 'Activ' : listing.status === 'draft' ? 'Draft' : 'Închiriat'}
                </span>
              </div>

              <div className="flex items-baseline mb-6" data-id="element-10082">
                <span className="text-4xl font-bold text-[#357BEB]" data-id="element-10083">
                  {listing.price}
                </span>
                <span className="text-lg text-gray-600 ml-2" data-id="element-10084">
                  {listing.currency}/lună
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-id="element-10085">
                <div className="bg-gray-50 rounded-lg p-3" data-id="element-10086">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-10087">Suprafață</p>
                  <p className="text-lg font-bold text-gray-900" data-id="element-10088">
                    {listing.surface} mp
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3" data-id="element-10089">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-10090">Camere</p>
                  <p className="text-lg font-bold text-gray-900" data-id="element-10091">
                    {listing.rooms}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3" data-id="element-10092">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-10093">Etaj</p>
                  <p className="text-lg font-bold text-gray-900" data-id="element-10094">
                    {listing.floor}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3" data-id="element-10095">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-10096">Mobilare</p>
                  <p className="text-sm font-semibold text-gray-900 capitalize" data-id="element-10097">
                    {listing.amenities.furnished}
                  </p>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-10098">
                <button className="flex items-center space-x-2 bg-[#357BEB] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#2868d8] transition-colors" data-id="element-10099">
                  <EditIcon className="w-5 h-5" data-id="element-10100" />
                  <span data-id="element-10101">Editează anunț</span>
                </button>
                <button onClick={() => setShowShareModal(true)} className="flex items-center space-x-2 bg-[#21A366] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#1a8555] transition-colors" data-id="element-10102">
                  <ShareIcon className="w-5 h-5" data-id="element-10103" />
                  <span data-id="element-10104">Trimite chiriaș</span>
                </button>
                <button className="flex items-center space-x-2 bg-white border-2 border-[#357BEB] text-[#357BEB] px-6 py-3 rounded-lg font-semibold hover:bg-[#357BEB] hover:text-white transition-all" data-id="element-10105">
                  <PhoneIcon className="w-5 h-5" data-id="element-10106" />
                  <span data-id="element-10107">{listing.ownerPhone}</span>
                </button>
              </div>
            </motion.div>

            {/* Owner Data Card - NEW */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl shadow-md p-6" data-id="element-10108">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2" data-id="element-10109">
                <UserIcon className="w-6 h-6 text-[#357BEB]" data-id="element-10110" />
                <span data-id="element-10111">Date Proprietar</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-10112">
                <div className="bg-white rounded-lg p-4" data-id="element-10113">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-10114">Telefon</p>
                  <a href={`tel:${listing.ownerPhone}`} className="text-lg font-bold text-[#357BEB] hover:underline flex items-center space-x-2" data-id="element-10115">
                    <PhoneIcon className="w-4 h-4" data-id="element-10116" />
                    <span data-id="element-10117">{listing.ownerPhone}</span>
                  </a>
                </div>
                <div className="bg-white rounded-lg p-4" data-id="element-10118">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-10119">ID Anunț</p>
                  <p className="text-lg font-bold text-gray-900" data-id="element-10120">
                    {listing.id}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4" data-id="element-10121">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-10122">Data Creare</p>
                  <p className="text-sm font-semibold text-gray-900" data-id="element-10123">
                    {new Date(listing.createdAt).toLocaleDateString('ro-RO')}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4" data-id="element-10124">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-10125">Status</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(listing.status)}`} data-id="element-10126">
                    {listing.status === 'active' ? 'Activ' : listing.status === 'draft' ? 'Draft' : 'Închiriat'}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Description */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="bg-white rounded-xl shadow-md p-6" data-id="element-10127">
              <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-10128">
                Descriere
              </h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line" data-id="element-10129">
                {listing.description || 'Nicio descriere disponibilă.'}
              </p>

              <div className="mt-6" data-id="element-10130">
                <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-10131">Dotări</h3>
                <div className="flex flex-wrap gap-2" data-id="element-10132">
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium capitalize" data-id="element-10133">
                    {listing.amenities.furnished}
                  </span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium capitalize" data-id="element-10134">
                    {listing.amenities.heating === 'centrala' ? 'Centrală proprie' : 'Termoficare'}
                  </span>
                  {listing.amenities.parking && <span className="px-3 py-1 bg-[#357BEB] bg-opacity-10 text-[#357BEB] rounded-full text-sm font-medium" data-id="element-10135">
                      Loc parcare
                    </span>}
                  {listing.amenities.balcony && <span className="px-3 py-1 bg-[#357BEB] bg-opacity-10 text-[#357BEB] rounded-full text-sm font-medium" data-id="element-10136">
                      Balcon
                    </span>}
                  {listing.amenities.pets && <span className="px-3 py-1 bg-[#21A366] bg-opacity-10 text-[#21A366] rounded-full text-sm font-medium" data-id="element-10137">
                      Animale acceptate
                    </span>}
                </div>
              </div>

              {sourceProspectingLead && <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-10138">
                  <h3 className="text-sm font-semibold text-gray-500 mb-2" data-id="element-10139">
                    Sursă
                  </h3>
                  <Link to="/inchiriere/liste-prospectare" className="inline-flex items-center space-x-2 text-[#357BEB] hover:underline" data-id="element-10140">
                    <ExternalLinkIcon className="w-4 h-4" data-id="element-10141" />
                    <span data-id="element-10142">
                      Din listă de prospectare (Lead #{sourceProspectingLead.id}
                      )
                    </span>
                  </Link>
                </div>}
            </motion.div>

            {/* Viewings */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="bg-white rounded-xl shadow-md p-6" data-id="element-10143">
              <div className="flex items-center justify-between mb-4" data-id="element-10144">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-10145">
                  Vizionări ({listingViewings.length})
                </h2>
                <button onClick={() => navigate(`/inchiriere/vizionari?listing=${listing.id}`)} className="flex items-center space-x-2 bg-[#357BEB] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#2868d8] transition-colors" data-id="element-10146">
                  <PlusCircleIcon className="w-4 h-4" data-id="element-10147" />
                  <span data-id="element-10148">Programează vizionare</span>
                </button>
              </div>

              {listingViewings.length > 0 ? <div className="space-y-3" data-id="element-10149">
                  {listingViewings.slice(0, 5).map(viewing => {
                const lead = tenantLeads.find(l => l.id === viewing.tenantLeadId);
                return <div key={viewing.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-id="element-10150">
                        <div className="flex items-center space-x-3" data-id="element-10151">
                          <div className="w-10 h-10 bg-[#FF6B35] bg-opacity-10 rounded-lg flex items-center justify-center" data-id="element-10152">
                            <CalendarIcon className="w-5 h-5 text-[#FF6B35]" data-id="element-10153" />
                          </div>
                          <div data-id="element-10154">
                            <p className="font-semibold text-gray-900" data-id="element-10155">
                              {lead?.name || 'Lead necunoscut'}
                            </p>
                            <p className="text-sm text-gray-600" data-id="element-10156">
                              {new Date(viewing.date).toLocaleDateString('ro-RO')}{' '}
                              la {viewing.time}
                            </p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getViewingStatusColor(viewing.status)}`} data-id="element-10157">
                          {viewing.status === 'scheduled' ? 'Programată' : viewing.status === 'completed' ? 'Finalizată' : 'Anulată'}
                        </span>
                      </div>;
              })}
                </div> : <p className="text-gray-500 text-center py-8" data-id="element-10158">
                  Nicio vizionare programată
                </p>}

              {listingViewings.length > 5 && <Link to={`/inchiriere/vizionari?listing=${listing.id}`} className="block text-center text-[#357BEB] hover:underline mt-4 font-medium" data-id="element-10159">
                  Vezi toate vizionările ({listingViewings.length})
                </Link>}
            </motion.div>
          </div>

          {/* Right Column - Leads */}
          <div className="space-y-6" data-id="element-10160">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="bg-white rounded-xl shadow-md p-6" data-id="element-10161">
              <div className="flex items-center justify-between mb-4" data-id="element-10162">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-10163">
                  Leaduri ({listingLeads.length})
                </h2>
                <button className="flex items-center space-x-2 bg-[#21A366] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#1a8555] transition-colors" data-id="element-10164">
                  <PlusCircleIcon className="w-4 h-4" data-id="element-10165" />
                  <span data-id="element-10166">Adaugă lead</span>
                </button>
              </div>

              {listingLeads.length > 0 ? <div className="space-y-3" data-id="element-10167">
                  {listingLeads.map(lead => <Link key={lead.id} to={`/inchiriere/leaduri/${lead.id}`} className="block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-10168">
                      <div className="flex items-start justify-between mb-2" data-id="element-10169">
                        <div data-id="element-10170">
                          <p className="font-semibold text-gray-900" data-id="element-10171">
                            {lead.name}
                          </p>
                          <p className="text-sm text-gray-600" data-id="element-10172">{lead.phone}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${lead.status === 'viewing' ? 'bg-[#FF6B35] bg-opacity-10 text-[#FF6B35]' : lead.status === 'qualified' ? 'bg-[#21A366] bg-opacity-10 text-[#21A366]' : 'bg-gray-200 text-gray-700'}`} data-id="element-10173">
                          {lead.status === 'new' ? 'Nou' : lead.status === 'qualified' ? 'Calificat' : lead.status === 'viewing' ? 'Vizionare' : lead.status}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600" data-id="element-10174">
                        <EuroIcon className="w-4 h-4 mr-1" data-id="element-10175" />
                        <span data-id="element-10176">Buget: {lead.budget} EUR</span>
                      </div>
                    </Link>)}
                </div> : <p className="text-gray-500 text-center py-8" data-id="element-10177">
                  Niciun lead asociat
                </p>}

              {listingLeads.length > 0 && <Link to={`/inchiriere/leaduri?listing=${listing.id}`} className="block text-center text-[#357BEB] hover:underline mt-4 font-medium" data-id="element-10178">
                  Vezi toate leadurile
                </Link>}
            </motion.div>

            {/* Quick Stats */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="bg-gradient-to-br from-[#357BEB] to-[#2868d8] rounded-xl shadow-md p-6 text-white" data-id="element-10179">
              <h3 className="text-lg font-bold mb-4" data-id="element-10180">Statistici rapide</h3>
              <div className="space-y-3" data-id="element-10181">
                <div className="flex items-center justify-between" data-id="element-10182">
                  <span className="text-white text-opacity-90" data-id="element-10183">
                    Leaduri active
                  </span>
                  <span className="text-2xl font-bold" data-id="element-10184">
                    {listingLeads.filter(l => ['new', 'qualified', 'viewing'].includes(l.status)).length}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-10185">
                  <span className="text-white text-opacity-90" data-id="element-10186">
                    Vizionări programate
                  </span>
                  <span className="text-2xl font-bold" data-id="element-10187">
                    {listingViewings.filter(v => v.status === 'scheduled').length}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-10188">
                  <span className="text-white text-opacity-90" data-id="element-10189">
                    Vizionări finalizate
                  </span>
                  <span className="text-2xl font-bold" data-id="element-10190">
                    {listingViewings.filter(v => v.status === 'completed').length}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Share Modal - NEW */}
      {showShareModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" data-id="element-10191">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8" data-id="element-10192">
            <div className="flex items-center justify-between mb-6" data-id="element-10193">
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-10194">
                Trimite Anunț către Chiriaș
              </h3>
              <button onClick={() => setShowShareModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-10195">
                <XIcon className="w-6 h-6 text-gray-600" data-id="element-10196" />
              </button>
            </div>

            <div className="space-y-6" data-id="element-10197">
              <div className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-200 rounded-xl p-6" data-id="element-10198">
                <div className="flex items-start space-x-3 mb-4" data-id="element-10199">
                  <InfoIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" data-id="element-10200" />
                  <div data-id="element-10201">
                    <h4 className="font-bold text-gray-900 mb-1" data-id="element-10202">
                      Link Public pentru Chiriași
                    </h4>
                    <p className="text-sm text-gray-700" data-id="element-10203">
                      Acest link NU conține datele proprietarului și poate fi
                      trimis în siguranță către potențiali chiriași.
                    </p>
                  </div>
                </div>
              </div>

              <div data-id="element-10204">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-10205">
                  Link Previzualizare Publică
                </label>
                <div className="flex items-center space-x-2" data-id="element-10206">
                  <input type="text" value={publicPreviewUrl} readOnly className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-mono text-sm" data-id="element-10207" />
                  <button onClick={handleCopyLink} className="flex items-center space-x-2 bg-[#357BEB] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#2868d8] transition-colors" data-id="element-10208">
                    {copiedLink ? <>
                        <CheckCircleIcon className="w-5 h-5" data-id="element-10209" />
                        <span data-id="element-10210">Copiat!</span>
                      </> : <>
                        <LinkIcon className="w-5 h-5" data-id="element-10211" />
                        <span data-id="element-10212">Copiază</span>
                      </>}
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-3" data-id="element-10213">
                <a href={publicPreviewUrl} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center space-x-2 bg-white border-2 border-[#357BEB] text-[#357BEB] px-6 py-3 rounded-lg font-semibold hover:bg-[#357BEB] hover:text-white transition-all" data-id="element-10214">
                  <EyeIcon className="w-5 h-5" data-id="element-10215" />
                  <span data-id="element-10216">Previzualizează</span>
                </a>
                <button onClick={() => setShowShareModal(false)} className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors" data-id="element-10217">
                  Închide
                </button>
              </div>
            </div>
          </motion.div>
        </div>}
    </div>;
}